import type {
  CategoriesEnvelope,
  Course,
  CourseCategory,
  CourseDetail,
  CourseDetailEnvelope,
  CoursesEnvelope,
  Lesson,
  CourseReview,
} from './types'
import {
  COURSES_FALLBACK,
  COURSE_CATEGORIES_FALLBACK,
  getFallbackCourse,
} from './data/fallback'

const API_URL = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '')

const toBool = (v: unknown) => v === 1 || v === true || v === '1'
const num = (v: unknown, fallback = 0) => {
  if (v == null || v === '') return fallback
  const n = Number(v)
  return Number.isFinite(n) ? n : fallback
}

function normalizeCourse(raw: Record<string, unknown>): Course {
  return {
    id: String(raw.id),
    title: String(raw.title ?? ''),
    titleEn: (raw.titleEn as string | null) ?? null,
    slug: String(raw.slug ?? ''),
    description: (raw.description as string | null) ?? null,
    descriptionEn: (raw.descriptionEn as string | null) ?? null,
    thumbnail: (raw.thumbnail as string | null) ?? null,
    price: num(raw.price),
    oldPrice: raw.oldPrice == null ? null : num(raw.oldPrice),
    instructorId: String(raw.instructorId ?? ''),
    categoryId: (raw.categoryId as string | null) ?? null,
    level: ((raw.level as string) ?? 'beginner') as Course['level'],
    duration: raw.duration == null ? null : num(raw.duration),
    isPublished: toBool(raw.isPublished),
    isFeatured: toBool(raw.isFeatured),
    sortOrder: num(raw.sortOrder),
    createdAt: String(raw.createdAt ?? ''),
    updatedAt: String(raw.updatedAt ?? ''),
    categoryName: (raw.categoryName as string | null) ?? null,
    categorySlug: (raw.categorySlug as string | null) ?? null,
    categoryIcon: (raw.categoryIcon as string | null) ?? null,
    instructorName: (raw.instructorName as string | null) ?? null,
    instructorAvatar: (raw.instructorAvatar as string | null) ?? null,
    instructorEmail: (raw.instructorEmail as string | null) ?? null,
    studentsCount: num(raw.studentsCount),
    lessonsCount: num(raw.lessonsCount),
    avgRating: raw.avgRating == null ? null : num(raw.avgRating),
    reviewsCount: num(raw.reviewsCount),
  }
}

function normalizeLesson(raw: Record<string, unknown>): Lesson {
  return {
    id: String(raw.id),
    courseId: String(raw.courseId ?? ''),
    title: String(raw.title ?? ''),
    titleEn: (raw.titleEn as string | null) ?? null,
    slug: String(raw.slug ?? ''),
    description: (raw.description as string | null) ?? null,
    descriptionEn: (raw.descriptionEn as string | null) ?? null,
    videoUrl: (raw.videoUrl as string | null) ?? null,
    videoProvider: (raw.videoProvider as Lesson['videoProvider']) ?? null,
    videoId: (raw.videoId as string | null) ?? null,
    videoEmbedUrl: (raw.videoEmbedUrl as string | null) ?? null,
    videoSignedUrl: (raw.videoSignedUrl as string | null) ?? null,
    duration: raw.duration == null ? null : num(raw.duration),
    sortOrder: num(raw.sortOrder),
    isPreview: toBool(raw.isPreview),
    createdAt: String(raw.createdAt ?? ''),
    updatedAt: String(raw.updatedAt ?? ''),
  }
}

function normalizeReview(raw: Record<string, unknown>): CourseReview {
  return {
    id: String(raw.id),
    courseId: String(raw.courseId ?? ''),
    userId: String(raw.userId ?? ''),
    rating: num(raw.rating),
    comment: (raw.comment as string | null) ?? null,
    isApproved: toBool(raw.isApproved),
    userName: (raw.userName as string | null) ?? null,
    userAvatar: (raw.userAvatar as string | null) ?? null,
    createdAt: String(raw.createdAt ?? ''),
  }
}

function normalizeCategory(raw: Record<string, unknown>): CourseCategory {
  return {
    id: String(raw.id),
    name: String(raw.name ?? ''),
    slug: String(raw.slug ?? ''),
    description: (raw.description as string | null) ?? null,
    icon: (raw.icon as string | null) ?? null,
    sortOrder: num(raw.sortOrder),
    createdAt: String(raw.createdAt ?? ''),
  }
}

export async function fetchCourses(
  signal?: AbortSignal,
): Promise<Course[]> {
  if (!API_URL) return COURSES_FALLBACK
  try {
    const res = await fetch(`${API_URL}/courses`, {
      signal,
      headers: { Accept: 'application/json' },
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const json = (await res.json()) as CoursesEnvelope
    if (!json.success || !json.data) throw new Error(json.error ?? 'Bad envelope')
    return json.data.map((row) =>
      normalizeCourse(row as unknown as Record<string, unknown>),
    )
  } catch (err) {
    if (signal?.aborted) throw err
    console.warn('[courses] falling back:', err)
    return COURSES_FALLBACK
  }
}

export async function fetchCategories(
  signal?: AbortSignal,
): Promise<CourseCategory[]> {
  if (!API_URL) return COURSE_CATEGORIES_FALLBACK
  try {
    const res = await fetch(`${API_URL}/courses/categories/list`, {
      signal,
      headers: { Accept: 'application/json' },
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const json = (await res.json()) as CategoriesEnvelope
    if (!json.success || !json.data) throw new Error(json.error ?? 'Bad envelope')
    return json.data.map((row) =>
      normalizeCategory(row as unknown as Record<string, unknown>),
    )
  } catch (err) {
    if (signal?.aborted) throw err
    return COURSE_CATEGORIES_FALLBACK
  }
}

export async function fetchCourseBySlug(
  slug: string,
  signal?: AbortSignal,
): Promise<CourseDetail | null> {
  if (!API_URL) return getFallbackCourse(slug)
  try {
    // Auth token attaches automatically here so the API can determine
    // enrollment state and decide whether to ship the lesson video URLs.
    const token = (() => {
      try {
        return JSON.parse(localStorage.getItem('abdallah-auth') ?? 'null')?.state
          ?.token as string | undefined
      } catch {
        return undefined
      }
    })()
    const res = await fetch(`${API_URL}/courses/${slug}`, {
      signal,
      headers: {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const json = (await res.json()) as CourseDetailEnvelope
    if (!json.success || !json.data) return null
    const raw = json.data as unknown as Record<string, unknown>
    const lessons = Array.isArray(raw.lessons)
      ? (raw.lessons as Record<string, unknown>[]).map(normalizeLesson)
      : []
    const reviews = Array.isArray(raw.reviews)
      ? (raw.reviews as Record<string, unknown>[]).map(normalizeReview)
      : []
    return {
      ...normalizeCourse(raw),
      isEnrolled: Boolean(raw.isEnrolled),
      lessons,
      reviews,
    }
  } catch (err) {
    if (signal?.aborted) throw err
    return getFallbackCourse(slug)
  }
}
